import { Play, Calendar, Mail, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";

interface FooterProps {
  content: {
    company: string;
    rights: string;
  };
}

const Footer = ({ content }: FooterProps) => {
  const { language } = useLanguage();
  
  return (
    <footer className="bg-brand-dark text-brand-light py-12">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          <div className="flex flex-col items-center gap-2">
            <img 
              src="/volym-studio-logo.svg" 
              alt="Volym Studio Logo" 
              className="w-12 h-12 invert"
            />
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">{language === "sv" ? "Tjänster" : "Services"}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/reklamfilm" className="hover:text-primary transition-colors">{language === "sv" ? "Reklamfilm" : "Commercial Film"}</Link></li>
              <li><Link to="/videoavatarer" className="hover:text-primary transition-colors">{language === "sv" ? "Videoavatar" : "Video Avatar"}</Link></li>
              <li><Link to="/explainer-video" className="hover:text-primary transition-colors">{language === "sv" ? "Explainer Videos" : "Explainer Videos"}</Link></li>
              <li><Link to="/animerad-film" className="hover:text-primary transition-colors">{language === "sv" ? "Animerad Film" : "Animated Film"}</Link></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">{language === "sv" ? "Företag" : "Company"}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/om-oss" className="hover:text-primary transition-colors">{language === "sv" ? "Om Oss" : "About Us"}</Link></li>
              <li><Link to="/pricing" className="hover:text-primary transition-colors">{language === "sv" ? "Priser" : "Pricing"}</Link></li>
              <li><a href="#contact" className="hover:text-primary transition-colors">{language === "sv" ? "Kontakt" : "Contact"}</a></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">{language === "sv" ? "Övrigt" : "Other"}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/kopvillkor" className="hover:text-primary transition-colors">{language === "sv" ? "Köpvillkor" : "Terms & Conditions"}</Link></li>
              <li><Link to="/cookie-policy" className="hover:text-primary transition-colors">Cookie Policy</Link></li>
              <li><Link to="/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</Link></li>
              <li><Link to="/sitemap" className="hover:text-primary transition-colors">Sitemap</Link></li>
            </ul>
          </div>
        </div>
        
        {/* Alternativa kontaktvägar section */}
        <div className="mb-6">
          
          <div className="grid md:grid-cols-3 gap-4 max-w-3xl mx-auto">
            <a 
              href="https://outlook.office.com/book/BokaMte1@volymkommunikation.se/?ismsaljsauthenabled" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
            >
              <Card className="bg-brand-darker hover:bg-brand-light/10 transition-all duration-300 hover:scale-105 cursor-pointer border-0">
                <CardContent className="p-4 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-brand-light/20 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-brand-light" />
                  </div>
                  <h3 className="font-bold text-brand-light text-lg mb-1">{language === "sv" ? "Boka möte" : "Book Meeting"}</h3>
                  <p className="text-brand-light/90 text-sm font-medium">
                    {language === "sv" ? "15 min gratis konsultation" : "15 min free consultation"}
                  </p>
                </CardContent>
              </Card>
            </a>
            
            <a href="mailto:info@volymkommunikation.se" className="block">
              <Card className="bg-brand-darker hover:bg-brand-light/10 transition-all duration-300 hover:scale-105 cursor-pointer border-0">
                <CardContent className="p-4 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-brand-light/20 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-brand-light" />
                  </div>
                  <h3 className="font-bold text-brand-light text-lg mb-1">Email</h3>
                  <p className="text-brand-light/90 text-sm font-medium">
                    info@volymkommunikation.se
                  </p>
                </CardContent>
              </Card>
            </a>
            
            <a href="tel:+46107502866" className="block">
              <Card className="bg-brand-darker hover:bg-brand-light/10 transition-all duration-300 hover:scale-105 cursor-pointer border-0">
                <CardContent className="p-4 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-brand-light/20 flex items-center justify-center">
                    <Phone className="w-6 h-6 text-brand-light" />
                  </div>
                  <h3 className="font-bold text-brand-light text-lg mb-1">{language === "sv" ? "Telefon" : "Phone"}</h3>
                  <p className="text-brand-light/90 text-sm font-medium">
                    +46 10 750 28 66
                  </p>
                </CardContent>
              </Card>
            </a>
          </div>
        </div>
        
        <div className="border-t border-border pt-8">
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} {content.company}. {content.rights}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;